import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, Mail, Building, FileText, Edit3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { getAllAvailableCourses, CourseOption } from '@/hooks/auth/enhancedCourseService';

const studentFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name must be less than 100 characters'),
  email: z.string().email('Please enter a valid email address'),
  department: z.string().optional(),
  notes: z.string().max(500, 'Notes must be less than 500 characters').optional(),
});

export type StudentFormData = z.infer<typeof studentFormSchema>;

interface StudentDetailsStepProps {
  selectedCourses: string[];
  onCoursesEdit: () => void;
  onFormChange: (data: StudentFormData) => void;
  initialData?: Partial<StudentFormData>;
}

const StudentDetailsStep: React.FC<StudentDetailsStepProps> = ({
  selectedCourses,
  onCoursesEdit,
  onFormChange,
  initialData
}) => {
  const allCourses = getAllAvailableCourses();
  
  const form = useForm<StudentFormData>({
    resolver: zodResolver(studentFormSchema),
    defaultValues: {
      name: initialData?.name || '',
      email: initialData?.email || '',
      department: initialData?.department || '',
      notes: initialData?.notes || '',
    },
    mode: 'onChange'
  });

  const { watch } = form;
  const formData = watch();

  React.useEffect(() => {
    if (form.formState.isValid) {
      onFormChange(formData);
    }
  }, [formData, form.formState.isValid, onFormChange]);

  const [selectedCourseDetails, setSelectedCourseDetails] = useState<CourseOption[]>([]);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const courses = await getAllAvailableCourses();
        const details = selectedCourses
          .map(courseId => courses.find(course => course.id === courseId))
          .filter(Boolean) as CourseOption[];
        setSelectedCourseDetails(details);
      } catch (error) {
        console.error('Failed to load courses:', error);
      }
    };
    
    loadCourses();
  }, [selectedCourses]);

  const getCategoryColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'react': return 'bg-blue-100 text-blue-800';
      case 'sql': return 'bg-purple-100 text-purple-800';
      case 'python': return 'bg-green-100 text-green-800';
      case 'ai': return 'bg-orange-100 text-orange-800';
      case 'devops': return 'bg-indigo-100 text-indigo-800';
      case 'appsec': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold">Student Information</h2>
        <p className="text-muted-foreground mt-2">
          Enter the student's details and review their course assignments.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Student Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="mr-2 h-5 w-5" />
              Student Details
            </CardTitle>
            <CardDescription>
              Provide the basic information for the new student account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name *</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Enter student's full name" 
                          {...field}
                          className="flex items-center"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address *</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input 
                            type="email"
                            placeholder="student@example.com" 
                            {...field}
                            className="pl-10"
                          />
                        </div>
                      </FormControl>
                      <FormDescription>
                        This will be their login email address.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="department"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Department (Optional)</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input 
                            placeholder="e.g. Computer Science, Engineering" 
                            {...field}
                            className="pl-10"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Additional Notes (Optional)</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Any additional information about the student..."
                          {...field}
                          className="min-h-[80px]"
                        />
                      </FormControl>
                      <FormDescription>
                        {field.value?.length || 0}/500 characters
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Course Review */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center">
                  <FileText className="mr-2 h-5 w-5" />
                  Course Assignments
                </CardTitle>
                <CardDescription>
                  Review the courses that will be assigned to this student.
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={onCoursesEdit}>
                <Edit3 className="mr-2 h-4 w-4" />
                Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {selectedCourseDetails.length > 0 ? (
              <div className="space-y-3">
                <div className="text-sm font-medium text-muted-foreground">
                  {selectedCourseDetails.length} course{selectedCourseDetails.length !== 1 ? 's' : ''} selected
                </div>
                
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {selectedCourseDetails.map(course => (
                    <div key={course.id} className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{course.title}</h4>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {course.description || 'No description available'}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Badge className={getCategoryColor(course.type)} variant="secondary">
                            {course.type?.toUpperCase() || 'GENERAL'}
                          </Badge>
                          <Badge variant="outline">
                            {course.level || 'Intermediate'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-sm font-medium">No courses selected</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Go back to select courses for this student.
                </p>
                <Button variant="outline" onClick={onCoursesEdit} className="mt-4">
                  Select Courses
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Form Validation Status */}
      <Card className={`border-l-4 ${form.formState.isValid ? 'border-l-green-500 bg-green-50' : 'border-l-yellow-500 bg-yellow-50'}`}>
        <CardContent className="pt-4">
          <div className="flex items-center">
            <div className={`w-2 h-2 rounded-full mr-2 ${form.formState.isValid ? 'bg-green-500' : 'bg-yellow-500'}`} />
            <span className="text-sm font-medium">
              {form.formState.isValid 
                ? 'All required information provided. Ready to proceed.' 
                : 'Please complete all required fields to continue.'}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentDetailsStep;